import { useCallback, useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useGetHistoryQuery } from "../../../store/messagingSlice"
import { addPageAction } from "../../../utils/newrelic"
import { NEWRELIC_SOURCE } from "../constants"
import { useMessagingView } from "./useMessagingView"

interface NewConversationTrackingReturn {
    isNewConversation: boolean
    setIsNewConversation: React.Dispatch<React.SetStateAction<boolean>>
    trackNewConversationMessageSent: () => void
    trackNewConversationExit: () => void
}

/** Hook for tracking new conversation events (entry, message sent, exit) in NewRelic. */
export const useNewConversationTracking = (): NewConversationTrackingReturn => {
    const { username } = useParams<{ username: string }>()
    const messagingView = useMessagingView()
    const {
        data: historyData,
        isFetching,
        isLoading,
        isUninitialized,
    } = useGetHistoryQuery({ username: username || "" }, { skip: !username })
    const [isNewConversation, setIsNewConversation] = useState(false)
    const [hasFiredMessageSent, setHasFiredMessageSent] = useState(false)
    const firedEntry = useRef(false)

    // Track when a new conversation is opened
    useEffect(() => {
        if (
            !isFetching &&
            !isLoading &&
            !isUninitialized &&
            historyData?.messages?.length === 0 &&
            !firedEntry.current
        ) {
            setIsNewConversation(true)
            addPageAction("DmOpenNewConversation", {
                actionType: "entry",
                source: messagingView.isMobile
                    ? NEWRELIC_SOURCE.Mobile
                    : NEWRELIC_SOURCE.Desktop,
            })
            firedEntry.current = true
        }
    }, [
        isFetching,
        isLoading,
        isUninitialized,
        historyData?.messages?.length,
        messagingView.isMobile,
        isNewConversation,
    ])

    const trackNewConversationMessageSent = useCallback(() => {
        if (isNewConversation && !hasFiredMessageSent) {
            addPageAction("DmOpenNewConversation", {
                actionType: "messageSent",
            })
            setHasFiredMessageSent(true)
        }
    }, [isNewConversation, hasFiredMessageSent])

    const trackNewConversationExit = useCallback(() => {
        if (isNewConversation) {
            addPageAction("DmOpenNewConversation", {
                actionType: "exit",
            })
        }
    }, [isNewConversation])

    return {
        isNewConversation,
        setIsNewConversation,
        trackNewConversationMessageSent,
        trackNewConversationExit,
    }
}
